import React, { FC, useEffect, useState } from "react";
import { Box, Button, Badge, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import {
	FilterList as FilterIcon,
	Download as DownloadIcon,
	PictureAsPdf as PdfIcon,
	TableChart as ExcelIcon,
	TableRows as CsvIcon,
	Description as WordIcon,
	Api as ApiIcon,
	KeyboardArrowDown as ArrowDownIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

interface IActionBarProps {
	onFilterClick: () => void;
	onExportExcel: (format: "excel" | "csv") => void;
	onExportPDF: () => void;
	onExportWord: () => void;
	filterCount: number;
	filterButtonRef: React.RefObject<HTMLButtonElement>;
	isExporting?: boolean;
	isGeneratingPDF?: boolean;
	isGeneratingWord?: boolean;
	onExportNicApiUsage: () => void;
	isExportingNicApiUsage?: boolean;
}

const ActionBar: FC<IActionBarProps> = ({
	onFilterClick,
	onExportExcel,
	onExportPDF,
	onExportWord,
	filterCount,
	filterButtonRef,
	isExporting = false,
	isGeneratingPDF = false,
	isGeneratingWord = false,
	onExportNicApiUsage,
	isExportingNicApiUsage = false
}) => {
	const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
	const [showNicApiUsageExport, setShowNicApiUsageExport] = useState<boolean>(false);
	const isExportMenuOpen = Boolean(exportMenuAnchor);
	const isAnyExportInProgress = isExporting || isGeneratingPDF || isGeneratingWord || isExportingNicApiUsage;

	// Hidden NIC API usage export, toggled with Ctrl/Cmd + Shift + U.
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === "u") {
				event.preventDefault();
				setShowNicApiUsageExport((prev) => !prev);
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, []);

	const handleExportMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setExportMenuAnchor(event.currentTarget);
	};

	const handleExportMenuClose = () => {
		setExportMenuAnchor(null);
	};

	const handleExcelExport = () => {
		handleExportMenuClose();
		onExportExcel("excel");
	};

	const handlePDFExport = () => {
		handleExportMenuClose();
		onExportPDF();
	};

	const handleWordExport = () => {
		handleExportMenuClose();
		onExportWord();
	};

	const handleNicApiUsageExport = () => {
		handleExportMenuClose();
		onExportNicApiUsage();
	};

	const handleCsvExport = () => {
		handleExportMenuClose();
		onExportExcel("csv");
	};

	return (
		<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 3 }}>
			<Box sx={{ display: "flex", gap: 1.5 }}>
				<Badge badgeContent={filterCount} color="primary">
					<Button
						ref={filterButtonRef}
						variant="outlined"
						startIcon={<FilterIcon />}
						onClick={onFilterClick}
						sx={{
							textTransform: "none",
							borderColor: "divider",
							color: "text.primary",
							"&:hover": {
								borderColor: "primary.main",
								bgcolor: "action.hover"
							}
						}}
					>
						Filters
					</Button>
				</Badge>

				<LoadingButton
					disableElevation
					variant="contained"
					startIcon={<DownloadIcon />}
					endIcon={<ArrowDownIcon />}
					onClick={handleExportMenuClick}
					loading={isAnyExportInProgress}
					loadingPosition="start"
					disabled={isAnyExportInProgress}
					sx={{
						textTransform: "none",
						bgcolor: "primary.main",
						"&:hover": { bgcolor: "primary.dark" }
					}}
				>
					{isGeneratingPDF
						? "Generating PDF..."
						: isGeneratingWord
						? "Generating Word..."
						: isExporting || isExportingNicApiUsage
						? "Exporting..."
						: "Export"}
				</LoadingButton>

				<Menu
					anchorEl={exportMenuAnchor}
					open={isExportMenuOpen}
					onClose={handleExportMenuClose}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "right"
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "right"
					}}
					slotProps={{
						paper: {
							sx: {
								mt: 1,
								minWidth: 160,
								boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)"
							}
						}
					}}
				>
					<MenuItem onClick={handlePDFExport} disabled={isAnyExportInProgress}>
						<ListItemIcon>
							<PdfIcon fontSize="small" sx={{ color: "#dc2626" }} />
						</ListItemIcon>
						<ListItemText>Export as PDF</ListItemText>
					</MenuItem>
					<MenuItem onClick={handleWordExport} disabled={isAnyExportInProgress}>
						<ListItemIcon>
							<WordIcon fontSize="small" sx={{ color: "#2563eb" }} />
						</ListItemIcon>
						<ListItemText>Export as Word</ListItemText>
					</MenuItem>
					<MenuItem onClick={handleExcelExport} disabled={isAnyExportInProgress}>
						<ListItemIcon>
							<ExcelIcon fontSize="small" sx={{ color: "#16a34a" }} />
						</ListItemIcon>
						<ListItemText>Export as Excel</ListItemText>
					</MenuItem>
					<MenuItem onClick={handleCsvExport} disabled={isAnyExportInProgress}>
						<ListItemIcon>
							<CsvIcon fontSize="small" sx={{ color: "#15803d" }} />
						</ListItemIcon>
						<ListItemText>Export as CSV</ListItemText>
					</MenuItem>
					{showNicApiUsageExport && (
						<MenuItem onClick={handleNicApiUsageExport} disabled={isAnyExportInProgress}>
							<ListItemIcon>
								<ApiIcon fontSize="small" sx={{ color: "#7c3aed" }} />
							</ListItemIcon>
							<ListItemText>NIC API Usage</ListItemText>
						</MenuItem>
					)}
				</Menu>
			</Box>
		</Box>
	);
};

export default ActionBar;
